/**
 * ============================================================================
 * EL-ARO
 * COMPONENT: Product Page Hero
 * ============================================================================
 *
 * Responsibilities:
 * - Product media gallery
 * - Thumbnail navigation
 * - Previous / next media
 * - Variant resolution
 * - Selected variant synchronization
 * - Quantity controls
 * - AJAX add to cart
 * - Native Shopify form fallback
 * - Add-to-cart loading / success / error states
 *
 * The component is intentionally scoped to:
 *
 *     .elaro-product-page-hero
 *
 * It does not modify existing product-card or drawer components.
 * ============================================================================
 */

class ElaroProductPageHero {

    constructor(section) {

        this.section = section;

        if (!this.section) return;

        this.form = this.section.querySelector(
            "[data-product-form]"
        );

        this.variantInput = this.section.querySelector(
            "[data-selected-variant-id-input]"
        );

        this.addButton = this.section.querySelector(
            "[data-add-to-cart]"
        );

        this.addButtonLabel = this.section.querySelector(
            "[data-add-to-cart-label]"
        );

        this.addButtonLoading = this.section.querySelector(
            "[data-add-to-cart-loading]"
        );

        this.buyButton = this.section.querySelector(
            "[data-buy-now]"
        );

        this.quantityInput = this.section.querySelector(
            "[data-quantity-input]"
        );

        this.mainMedia = this.section.querySelector(
            "[data-product-main-media]"
        );

        this.thumbnailTrack = this.section.querySelector(
            "[data-product-thumbnails]"
        );

        this.previousButton = this.section.querySelector(
            "[data-gallery-previous]"
        );

        this.nextButton = this.section.querySelector(
            "[data-gallery-next]"
        );

        this.mediaCounter = this.section.querySelector(
            "[data-current-media-index]"
        );

        this.priceContainer = this.section.querySelector(
            "[data-product-price]"
        );

        this.skuElement = this.section.querySelector(
            "[data-product-sku]"
        );

        this.optionInputs = this.section.querySelectorAll(
            "[data-product-option]"
        );

        this.thumbnailButtons = this.section.querySelectorAll(
            "[data-media-id]"
        );

        this.variants = this.getJSON(
            "[data-product-variants]"
        );

        this.media = this.getJSON(
            "[data-product-media]"
        );

        this.currentMediaIndex = this.getInitialMediaIndex();

        this.currentVariant = this.getInitialVariant();

        this.isSubmitting = false;

        this.bindEvents();

        this.initialize();

    }


    /*==========================================================================
      INITIALIZATION
    ==========================================================================*/

    initialize() {

        if (!this.currentVariant) return;

        this.syncVariantUI();

        this.syncQuantity();

        this.showMedia(
            this.currentMediaIndex,
            false
        );

        this.updatePurchaseState();

    }


    /*==========================================================================
      EVENTS
    ==========================================================================*/

    bindEvents() {

        /*
         * Gallery thumbnails
         */

        this.thumbnailButtons.forEach(
            button => {

                button.addEventListener(
                    "click",
                    event => {

                        event.preventDefault();

                        const index = Number(
                            button.dataset.mediaIndex
                        );

                        this.showMedia(
                            index,
                            true
                        );

                    }
                );

            }
        );


        /*
         * Previous gallery button
         */

        if (this.previousButton) {

            this.previousButton.addEventListener(
                "click",
                event => {

                    event.preventDefault();

                    this.showPreviousMedia();

                }
            );

        }


        /*
         * Next gallery button
         */

        if (this.nextButton) {

            this.nextButton.addEventListener(
                "click",
                event => {

                    event.preventDefault();

                    this.showNextMedia();

                }
            );

        }


        /*
         * Keyboard navigation
         */

        if (this.thumbnailTrack) {

            this.thumbnailTrack.addEventListener(
                "keydown",
                event => {

                    if (
                        event.key !== "ArrowLeft" &&
                        event.key !== "ArrowRight"
                    ) {

                        return;

                    }

                    event.preventDefault();

                    if (event.key === "ArrowLeft") {

                        this.showPreviousMedia();

                    } else {

                        this.showNextMedia();

                    }

                }
            );

        }


        /*
         * Variant options
         */

        this.optionInputs.forEach(
            input => {

                input.addEventListener(
                    "change",
                    () => {

                        this.handleVariantChange();

                    }
                );

            }
        );


        /*
         * Quantity decrease
         */

        const minusButton = this.section.querySelector(
            "[data-quantity-minus]"
        );

        if (minusButton) {

            minusButton.addEventListener(
                "click",
                event => {

                    event.preventDefault();

                    this.changeQuantity(-1);

                }
            );

        }


        /*
         * Quantity increase
         */

        const plusButton = this.section.querySelector(
            "[data-quantity-plus]"
        );

        if (plusButton) {

            plusButton.addEventListener(
                "click",
                event => {

                    event.preventDefault();

                    this.changeQuantity(1);

                }
            );

        }


        /*
         * Quantity input
         */

        if (this.quantityInput) {

            this.quantityInput.addEventListener(
                "change",
                () => {

                    this.normalizeQuantity();

                }
            );

            this.quantityInput.addEventListener(
                "input",
                () => {

                    this.normalizeQuantity();

                }
            );

        }


        /*
         * Product form
         *
         * Native Shopify form remains available as fallback.
         */

        if (this.form) {

            this.form.addEventListener(
                "submit",
                event => {

                    if (
                        event.submitter &&
                        event.submitter.name === "checkout"
                    ) {

                        return;

                    }

                    this.handleAddToCart(
                        event
                    );

                }
            );

        }


        /*
         * Touch swipe
         */

        this.bindTouchEvents();

    }


    /*==========================================================================
      JSON
    ==========================================================================*/

    getJSON(selector) {

        const element = this.section.querySelector(
            selector
        );

        if (!element) return [];

        try {

            return JSON.parse(
                element.textContent
            );

        } catch (error) {

            console.error(
                "EL-ARO Product Hero: Invalid JSON",
                error
            );

            return [];

        }

    }


    /*==========================================================================
      INITIAL VARIANT
    ==========================================================================*/

    getInitialVariant() {

        const variantId = Number(
            this.section.dataset.selectedVariantId
        );

        if (variantId) {

            const matchingVariant = this.variants.find(
                variant =>
                    Number(variant.id) === variantId
            );

            if (matchingVariant) {

                return matchingVariant;

            }

        }

        return (
            this.variants.find(
                variant => variant.available
            ) ||
            this.variants[0] ||
            null
        );

    }


    /*==========================================================================
      INITIAL MEDIA
    ==========================================================================*/

    getInitialMediaIndex() {

        const featuredMediaId = this.currentVariant
            ? Number(
                this.currentVariant.featured_media?.id
            )
            : null;

        if (featuredMediaId) {

            const index = this.media.findIndex(
                media =>
                    Number(media.id) === featuredMediaId
            );

            if (index >= 0) {

                return index;

            }

        }

        return 0;

    }


    /*==========================================================================
      GALLERY
    ==========================================================================*/

    showMedia(index, shouldScroll = true) {

        if (!this.media.length) return;

        const mediaCount = this.media.length;

        if (index < 0) {

            index = mediaCount - 1;

        }

        if (index >= mediaCount) {

            index = 0;

        }

        this.currentMediaIndex = index;

        const media = this.media[index];

        if (!media) return;

        this.renderMainMedia(
            media
        );

        this.updateThumbnails(
            media.id,
            index,
            shouldScroll
        );

        this.updateMediaCounter(
            index
        );

        this.updateGalleryButtons();

    }


    /*==========================================================================
      MAIN MEDIA
    ==========================================================================*/

    renderMainMedia(media) {

        if (!this.mainMedia) return;

        const currentElement =
            this.mainMedia.firstElementChild;

        if (
            currentElement &&
            currentElement.dataset.mediaRendered === String(media.id)
        ) {

            return;

        }

        const existingMedia = currentElement;

        if (existingMedia) {

            existingMedia.style.opacity = "0";

        }

        window.setTimeout(
            () => {

                this.mainMedia.innerHTML =
                    this.createMediaMarkup(
                        media
                    );

                const newMedia =
                    this.mainMedia.firstElementChild;

                if (!newMedia) return;

                newMedia.dataset.mediaRendered =
                    String(media.id);

                newMedia.style.opacity = "0";

                requestAnimationFrame(
                    () => {

                        newMedia.style.opacity = "1";

                    }
                );

            },
            existingMedia ? 120 : 0
        );

    }


    /*==========================================================================
      MEDIA MARKUP
    ==========================================================================*/

    createMediaMarkup(media) {

        const escapedAlt =
            this.escapeHTML(
                media.alt || ""
            );

        const escapedSrc =
            this.escapeHTML(
                media.src || ""
            );

        if (media.type === "image") {

            return `
                <img
                    class="elaro-product-page-hero__main-image"
                    src="${escapedSrc}"
                    alt="${escapedAlt}"
                    loading="eager"
                    decoding="async"
                >
            `;

        }


        /*
         * The media JSON intentionally contains preview images for
         * non-image media. We use the preview image here rather than
         * fabricating unsupported source URLs.
         */

        if (media.src) {

            return `
                <img
                    class="elaro-product-page-hero__main-image"
                    src="${escapedSrc}"
                    alt="${escapedAlt}"
                    loading="eager"
                    decoding="async"
                >
            `;

        }

        return `
            <div
                class="elaro-product-page-hero__media-placeholder"
                role="img"
                aria-label="${escapedAlt}"
            >
            </div>
        `;

    }


    /*==========================================================================
      PREVIOUS MEDIA
    ==========================================================================*/

    showPreviousMedia() {

        this.showMedia(
            this.currentMediaIndex - 1,
            true
        );

    }


    /*==========================================================================
      NEXT MEDIA
    ==========================================================================*/

    showNextMedia() {

        this.showMedia(
            this.currentMediaIndex + 1,
            true
        );

    }


    /*==========================================================================
      THUMBNAILS
    ==========================================================================*/

    updateThumbnails(
        activeMediaId,
        activeIndex,
        shouldScroll
    ) {

        this.thumbnailButtons.forEach(
            button => {

                const isActive =
                    Number(button.dataset.mediaId) ===
                    Number(activeMediaId);

                button.classList.toggle(
                    "is-active",
                    isActive
                );

                button.setAttribute(
                    "aria-current",
                    isActive
                        ? "true"
                        : "false"
                );

                if (
                    isActive &&
                    shouldScroll
                ) {

                    this.scrollThumbnailIntoView(
                        button
                    );

                }

            }
        );

    }


    /*==========================================================================
      THUMBNAIL SCROLL
    ==========================================================================*/

    scrollThumbnailIntoView(button) {

        if (!this.thumbnailTrack) return;

        const trackRect =
            this.thumbnailTrack.getBoundingClientRect();

        const buttonRect =
            button.getBoundingClientRect();

        const isOutsideLeft =
            buttonRect.left <
            trackRect.left;

        const isOutsideRight =
            buttonRect.right >
            trackRect.right;

        if (
            isOutsideLeft ||
            isOutsideRight
        ) {

            button.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
                inline: "center"
            });

        }

    }


    /*==========================================================================
      MEDIA COUNTER
    ==========================================================================*/

    updateMediaCounter(index) {

        if (!this.mediaCounter) return;

        this.mediaCounter.textContent =
            String(index + 1);

    }


    /*==========================================================================
      GALLERY BUTTON STATE
    ==========================================================================*/

    updateGalleryButtons() {

        if (!this.previousButton) return;

        const hasMultipleMedia =
            this.media.length > 1;

        this.previousButton.disabled =
            !hasMultipleMedia;

        if (this.nextButton) {

            this.nextButton.disabled =
                !hasMultipleMedia;

        }

    }


    /*==========================================================================
      TOUCH
    ==========================================================================*/

    bindTouchEvents() {

        if (!this.mainMedia) return;

        let startX = 0;
        let startY = 0;
        let isTracking = false;

        this.mainMedia.addEventListener(
            "touchstart",
            event => {

                const touch =
                    event.changedTouches[0];

                startX = touch.clientX;
                startY = touch.clientY;

                isTracking = true;

            },
            {
                passive: true
            }
        );


        this.mainMedia.addEventListener(
            "touchend",
            event => {

                if (!isTracking) return;

                isTracking = false;

                const touch =
                    event.changedTouches[0];

                const deltaX =
                    touch.clientX - startX;

                const deltaY =
                    touch.clientY - startY;

                if (
                    Math.abs(deltaX) < 45 ||
                    Math.abs(deltaX) < Math.abs(deltaY)
                ) {

                    return;

                }

                if (deltaX < 0) {

                    this.showNextMedia();

                } else {

                    this.showPreviousMedia();

                }

            },
            {
                passive: true
            }
        );

    }


    /*==========================================================================
      VARIANT CHANGE
    ==========================================================================*/

    handleVariantChange() {

        const selectedOptions =
            this.getSelectedOptions();

        const matchingVariant =
            this.findVariant(
                selectedOptions
            );

        if (!matchingVariant) {

            this.currentVariant = null;

            this.updatePurchaseState();

            return;

        }

        this.currentVariant =
            matchingVariant;

        this.updateVariantInput();

        this.updateVariantURL();

        this.updatePrice();

        this.updateSKU();

        this.updatePurchaseState();

        this.updateVariantMedia();

    }


    /*==========================================================================
      SELECTED OPTIONS
    ==========================================================================*/

    getSelectedOptions() {

        const options = [];

        const optionGroups =
            this.section.querySelectorAll(
                "[data-option-index]"
            );

        optionGroups.forEach(
            group => {

                const checked =
                    group.querySelector(
                        "[data-product-option]:checked"
                    );

                options.push(
                    checked
                        ? checked.value
                        : null
                );

            }
        );

        return options;

    }


    /*==========================================================================
      FIND VARIANT
    ==========================================================================*/

    findVariant(options) {

        if (!this.variants.length) {

            return null;

        }

        return this.variants.find(
            variant => {

                return options.every(
                    (option, index) => {

                        if (option === null) {

                            return true;

                        }

                        return (
                            variant.options[index] ===
                            option
                        );

                    }
                );

            }
        ) || null;

    }


    /*==========================================================================
      VARIANT INPUT
    ==========================================================================*/

    updateVariantInput() {

        if (!this.variantInput) return;

        this.variantInput.value =
            this.currentVariant.id;

        this.variantInput.dispatchEvent(
            new Event(
                "change",
                {
                    bubbles: true
                }
            )
        );

        this.section.dataset.selectedVariantId =
            this.currentVariant.id;

    }


    /*==========================================================================
      VARIANT UI
    ==========================================================================*/

    syncVariantUI() {

        if (!this.currentVariant) return;

        this.optionInputs.forEach(
            input => {

                const optionIndex =
                    Number(
                        input
                            .closest(
                                "[data-option-index]"
                            )
                            ?.dataset.optionIndex
                    );

                const isSelected =
                    this.currentVariant.options[
                        optionIndex
                    ] === input.value;

                input.checked =
                    isSelected;

                const label =
                    input.closest(
                        ".elaro-product-page-hero__option-value"
                    );

                if (label) {

                    label.classList.toggle(
                        "is-selected",
                        isSelected
                    );

                }

            }
        );

    }


    /*==========================================================================
      OPTION AVAILABILITY
    ==========================================================================*/

    updateOptionAvailability() {

        const selectedOptions =
            this.getSelectedOptions();

        const groups =
            this.section.querySelectorAll(
                "[data-option-index]"
            );

        groups.forEach(
            group => {

                const optionIndex =
                    Number(
                        group.dataset.optionIndex
                    );

                const inputs =
                    group.querySelectorAll(
                        "[data-product-option]"
                    );

                inputs.forEach(
                    input => {

                        const candidateValue =
                            input.value;

                        const possible =
                            this.variants.some(
                                variant => {

                                    if (
                                        !variant.available
                                    ) {

                                        return false;

                                    }

                                    return variant.options.every(
                                        (
                                            variantOption,
                                            index
                                        ) => {

                                            if (
                                                index ===
                                                optionIndex
                                            ) {

                                                return (
                                                    variantOption ===
                                                    candidateValue
                                                );

                                            }

                                            const selected =
                                                selectedOptions[
                                                    index
                                                ];

                                            if (
                                                selected ===
                                                null
                                            ) {

                                                return true;

                                            }

                                            return (
                                                variantOption ===
                                                selected
                                            );

                                        }
                                    );

                                }
                            );

                        input.disabled =
                            !possible;

                        const label =
                            input.closest(
                                ".elaro-product-page-hero__option-value"
                            );

                        if (label) {

                            label.classList.toggle(
                                "is-unavailable",
                                !possible
                            );

                        }

                    }
                );

            }
        );

    }


    /*==========================================================================
      PRICE
    ==========================================================================*/

    updatePrice() {

        if (
            !this.priceContainer ||
            !this.currentVariant
        ) {

            return;

        }

        const price =
            this.formatMoney(
                this.currentVariant.price
            );

        const comparePrice =
            this.currentVariant.compare_at_price;

        let html = `
            <span class="elaro-product-page-hero__price-current">
                ${price}
            </span>
        `;

        if (
            comparePrice &&
            comparePrice > this.currentVariant.price
        ) {

            html += `
                <span class="elaro-product-page-hero__price-compare">
                    ${this.formatMoney(comparePrice)}
                </span>
            `;

        }

        this.priceContainer.innerHTML =
            html;

    }


    /*==========================================================================
      MONEY
    ==========================================================================*/

    formatMoney(cents) {

        if (
            window.Shopify &&
            typeof window.Shopify.formatMoney ===
                "function"
        ) {

            return window.Shopify.formatMoney(
                cents
            );

        }

        const currency =
            document.documentElement.dataset.currency ||
            "INR";

        return new Intl.NumberFormat(
            undefined,
            {
                style: "currency",
                currency: currency
            }
        ).format(
            Number(cents) / 100
        );

    }


    /*==========================================================================
      SKU
    ==========================================================================*/

    updateSKU() {

        if (!this.skuElement) return;

        this.skuElement.textContent =
            this.currentVariant?.sku || "";

    }


    /*==========================================================================
      VARIANT MEDIA
    ==========================================================================*/

    updateVariantMedia() {

        if (!this.currentVariant) return;

        const featuredMediaId =
            this.currentVariant.featured_media?.id;

        if (!featuredMediaId) return;

        const mediaIndex =
            this.media.findIndex(
                media =>
                    Number(media.id) ===
                    Number(featuredMediaId)
            );

        if (mediaIndex >= 0) {

            this.showMedia(
                mediaIndex,
                true
            );

        }

    }


    /*==========================================================================
      PURCHASE STATE
    ==========================================================================*/

    updatePurchaseState() {

        const available =
            Boolean(
                this.currentVariant &&
                this.currentVariant.available
            );

        if (this.addButton) {

            this.addButton.disabled =
                !available ||
                this.isSubmitting;

        }

        if (this.buyButton) {

            this.buyButton.disabled =
                !available;

        }

        if (this.addButtonLabel) {

            if (!this.currentVariant) {

                this.addButtonLabel.textContent =
                    "UNAVAILABLE";

            } else if (
                !this.currentVariant.available
            ) {

                this.addButtonLabel.textContent =
                    "SOLD OUT";

            } else {

                this.addButtonLabel.textContent =
                    this.section.dataset.addText ||
                    "ADD TO CART";

            }

        }

    }


    /*==========================================================================
      ADD TO CART
    ==========================================================================*/

    async handleAddToCart(event) {

        event.preventDefault();

        if (this.isSubmitting) return;

        if (
            !this.currentVariant ||
            !this.currentVariant.available
        ) {

            this.showError(
                "This product is currently unavailable."
            );

            return;

        }

        this.isSubmitting = true;

        this.setLoadingState(
            true
        );

        try {

            const formData =
                new FormData(
                    this.form
                );

            formData.set(
                "id",
                String(
                    this.currentVariant.id
                )
            );

            formData.set(
                "quantity",
                String(
                    this.getQuantity()
                )
            );


            /*
             * Shopify AJAX Cart API
             */

            const response =
                await fetch(
                    `${window.Shopify?.routes?.root || "/"}cart/add.js`,
                    {
                        method: "POST",
                        headers: {
                            "Accept":
                                "application/json"
                        },
                        body: formData
                    }
                );


            const data =
                await response.json();


            if (!response.ok) {

                throw new Error(
                    data.description ||
                    data.message ||
                    "Unable to add this product to the cart."
                );

            }


            /*
             * Notify other EL-ARO / Shopify components.
             */

            this.dispatchCartEvents(
                data
            );


            this.showSuccess();

        } catch (error) {

            console.error(
                "EL-ARO Product Hero Add to Cart:",
                error
            );

            this.showError(
                error.message ||
                "Unable to add this product to the cart."
            );

        } finally {

            this.isSubmitting = false;

            this.setLoadingState(
                false
            );

        }

    }


    /*==========================================================================
      CART EVENTS
    ==========================================================================*/

    dispatchCartEvents(
        addedItem
    ) {

        document.dispatchEvent(
            new CustomEvent(
                "elaro:cart:item-added",
                {
                    bubbles: true,
                    detail: {
                        item: addedItem,
                        variant: this.currentVariant
                    }
                }
            )
        );


        document.dispatchEvent(
            new CustomEvent(
                "cart:refresh",
                {
                    bubbles: true,
                    detail: {
                        item: addedItem
                    }
                }
            )
        );


        document.dispatchEvent(
            new CustomEvent(
                "cart:updated",
                {
                    bubbles: true,
                    detail: {
                        item: addedItem
                    }
                }
            )
        );


        /*
         * Shopify-compatible cart update event.
         */

        document.dispatchEvent(
            new CustomEvent(
                "cart:refresh",
                {
                    bubbles: true
                }
            )
        );

    }


    /*==========================================================================
      LOADING
    ==========================================================================*/

    setLoadingState(
        loading
    ) {

        if (!this.addButton) return;

        this.addButton.classList.toggle(
            "is-loading",
            loading
        );

        this.addButton.setAttribute(
            "aria-busy",
            loading
                ? "true"
                : "false"
        );

        this.addButton.disabled =
            loading ||
            !this.currentVariant ||
            !this.currentVariant.available;

    }


    /*==========================================================================
      SUCCESS
    ==========================================================================*/

    showSuccess() {

        if (!this.addButtonLabel) return;

        const originalText =
            this.addButtonLabel.dataset.originalText ||
            this.addButtonLabel.textContent;

        this.addButtonLabel.dataset.originalText =
            originalText;

        this.addButtonLabel.textContent =
            "ADDED TO CART";

        this.addButton.classList.add(
            "is-added"
        );

        window.setTimeout(
            () => {

                if (!this.addButtonLabel) return;

                this.addButtonLabel.textContent =
                    originalText;

                this.addButton.classList.remove(
                    "is-added"
                );

            },
            1800
        );

    }


    /*==========================================================================
      ERROR
    ==========================================================================*/

    showError(
        message
    ) {

        if (!this.addButtonLabel) return;

        const originalText =
            this.addButtonLabel.dataset.originalText ||
            this.addButtonLabel.textContent;

        this.addButtonLabel.dataset.originalText =
            originalText;

        this.addButtonLabel.textContent =
            "TRY AGAIN";

        this.addButton.classList.add(
            "is-error"
        );

        this.addButton.setAttribute(
            "aria-label",
            message
        );

        window.setTimeout(
            () => {

                if (!this.addButtonLabel) return;

                this.addButtonLabel.textContent =
                    originalText;

                this.addButton.classList.remove(
                    "is-error"
                );

                this.addButton.removeAttribute(
                    "aria-label"
                );

            },
            2200
        );

    }


    /*==========================================================================
      QUANTITY
    ==========================================================================*/

    changeQuantity(
        amount
    ) {

        if (!this.quantityInput) return;

        const current =
            Number(
                this.quantityInput.value
            ) || 1;

        const next =
            Math.max(
                1,
                current + amount
            );

        this.quantityInput.value =
            next;

        this.quantityInput.dispatchEvent(
            new Event(
                "change",
                {
                    bubbles: true
                }
            )
        );

    }


    /*==========================================================================
      QUANTITY NORMALIZATION
    ==========================================================================*/

    normalizeQuantity() {

        if (!this.quantityInput) return;

        let quantity =
            parseInt(
                this.quantityInput.value,
                10
            );

        if (
            Number.isNaN(quantity) ||
            quantity < 1
        ) {

            quantity = 1;

        }

        this.quantityInput.value =
            quantity;

    }


    /*==========================================================================
      QUANTITY
    ==========================================================================*/

    getQuantity() {

        this.normalizeQuantity();

        return Math.max(
            1,
            Number(
                this.quantityInput?.value
            ) || 1
        );

    }


    /*==========================================================================
      SYNC QUANTITY
    ==========================================================================*/

    syncQuantity() {

        if (!this.quantityInput) return;

        this.normalizeQuantity();

    }


    /*==========================================================================
      UPDATE URL
    ==========================================================================*/

    updateVariantURL() {

        if (!this.currentVariant) return;

        const url =
            new URL(
                window.location.href
            );

        url.searchParams.set(
            "variant",
            this.currentVariant.id
        );

        window.history.replaceState(
            {
                variant:
                    this.currentVariant.id
            },
            "",
            url.toString()
        );

    }


    /*==========================================================================
      ESCAPE HTML
    ==========================================================================*/

    escapeHTML(
        value
    ) {

        return String(value)
            .replace(
                /&/g,
                "&amp;"
            )
            .replace(
                /</g,
                "&lt;"
            )
            .replace(
                />/g,
                "&gt;"
            )
            .replace(
                /"/g,
                "&quot;"
            )
            .replace(
                /'/g,
                "&#039;"
            );

    }

}


/*==============================================================================
  INITIALIZE
==============================================================================*/

function initializeElaroProductPageHero() {

    const sections =
        document.querySelectorAll(
            ".elaro-product-page-hero"
        );

    sections.forEach(
        section => {

            if (
                section.dataset.elaroProductHeroInitialized ===
                "true"
            ) {

                return;

            }

            section.dataset.elaroProductHeroInitialized =
                "true";

            new ElaroProductPageHero(
                section
            );

        }
    );

}


/*==============================================================================
  DOM READY
==============================================================================*/

if (
    document.readyState ===
    "loading"
) {

    document.addEventListener(
        "DOMContentLoaded",
        initializeElaroProductPageHero
    );

} else {

    initializeElaroProductPageHero();

}


/*==============================================================================
  SHOPIFY SECTION EVENTS
==============================================================================*/

/*
 * Important for Shopify Theme Editor / Section Rendering API.
 *
 * If Shopify dynamically re-renders this section, initialize the new
 * instance without touching other EL-ARO components.
 */

document.addEventListener(
    "shopify:section:load",
    event => {

        const section =
            event.target.querySelector(
                ".elaro-product-page-hero"
            );

        if (!section) return;

        section.dataset.elaroProductHeroInitialized =
            "false";

        initializeElaroProductPageHero();

    }
);